// Firebase client bootstrap.
// Config comes from Vite env vars (VITE_FIREBASE_*) — never hardcoded.
// All values here are publishable client identifiers, not secrets.
import { initializeApp, getApps, getApp, type FirebaseApp } from "firebase/app";
import { getAuth, type Auth } from "firebase/auth";
import { getAnalytics, isSupported as analyticsSupported, type Analytics } from "firebase/analytics";
import { getMessaging, isSupported as messagingSupported, getToken, type Messaging } from "firebase/messaging";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

export const isFirebaseConfigured = Boolean(
  firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId,
);

export const firebaseApp: FirebaseApp | null = isFirebaseConfigured
  ? getApps().length
    ? getApp()
    : initializeApp(firebaseConfig)
  : null;

export const firebaseAuth: Auth | null = firebaseApp ? getAuth(firebaseApp) : null;

/** Analytics only works in a supported browser context. Returns null otherwise. */
export async function getFirebaseAnalytics(): Promise<Analytics | null> {
  if (!firebaseApp || typeof window === "undefined") return null;
  try {
    return (await analyticsSupported()) ? getAnalytics(firebaseApp) : null;
  } catch {
    return null;
  }
}

/** Messaging requires a service worker + supported browser. Returns null otherwise. */
export async function getFirebaseMessaging(): Promise<Messaging | null> {
  if (!firebaseApp || typeof window === "undefined") return null;
  try {
    return (await messagingSupported()) ? getMessaging(firebaseApp) : null;
  } catch {
    return null;
  }
}

/** Request a device token for push. Needs VITE_FIREBASE_VAPID_KEY and granted permission. */
export async function getDeviceToken(): Promise<string | null> {
  const messaging = await getFirebaseMessaging();
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  if (!messaging || !vapidKey) return null;
  try {
    if (Notification.permission !== "granted") {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") return null;
    }
    return await getToken(messaging, { vapidKey });
  } catch {
    return null;
  }
}
